import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, withModifiers as _withModifiers, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, isRef as _isRef, createVNode as _createVNode, toDisplayString as _toDisplayString, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-38d25d96"),n=n(),_popScopeId(),n)
const _hoisted_1 = ["onChange", "accept"]
const _hoisted_2 = {
  key: 0,
  class: "files-upload__group"
}
const _hoisted_3 = { class: "files-upload__allowed-text" }

import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { useInterviewStore } from '@/stores/interviewStore'
import { UploadedFileModel, UploadedFilesResponse, isUploadError } from '@/models/AdditionalDataTypes'
// import ImagePreview from '@/components/Shared/ImagePreview.vue'
import ImageEdit from '@/components/Shared/ImageEdit.vue'
import UploadProgressBar from '@/components/Shared/UploadProgressBar.vue'
import FlatButton from '@/components/Shared/FlatButton.vue'
import WarningTextBlock from '@/components/Shared/WarningTextBlock.vue'
import { InterviewQuestionResponseModel } from '@/models/QuestionModels'
import { scrollToElementBottom } from '../../scrollToElementBottom'


export default /*#__PURE__*/_defineComponent({
  __name: 'UploadMultipleFilesComponent',
  props: {
    descrInputPlaceholder: { default: '' },
    maxFileSizeBytes: { default: 25 * 1000 * 1000 },
    supportedFileTypes: null,
    question: null
  },
  emits: ['handleFilesChanged', 'handleError'],
  setup(__props: any, { emit: emits }) {

const props = __props as { descrInputPlaceholder: string, maxFileSizeBytes: number, supportedFileTypes: string[], question: InterviewQuestionResponseModel };



const isSubmited = ref<boolean>(false)



const interviewStore = useInterviewStore()

const errorKeyPrefix = 'InterviewQuestionary.UploadFiles.Errors.'

/**
 * Copy quoting the size limit uses this token so the number always matches what is actually
 * enforced. Hardcoding it in the dictionary is what let the copy claim 250MB while the wizard
 * was configured for 2000MB and the server allowed 50MB.
 *
 * Substitution is deliberately tolerant: text without the token is returned untouched, so
 * dictionary entries that have not been migrated to the token still render normally.
 */
const maxFileSizeToken = '{0}'

function withMaxFileSize(text: string) {
    if (!text) {
        return text
    }

    // split/join rather than replaceAll, which needs a polyfill in this build's browser targets.
    return text.split(maxFileSizeToken).join(String(interviewStore.maxFileSizeMb))
}

/**
 * Last-resort text if a key is missing from the dictionary, or from the translations
 * array in QuestionaryService. That combination is what caused TACM-1524: an empty
 * string makes the WarningTextBlock v-if false, so the error renders as nothing at all.
 * Falling back to untranslated text keeps the failure visible instead of silent.
 */
const errorFallbacks: { [key: string]: string } = {
    FileToLarge: 'The file is too large. Please upload a file up to {0} MB',
    InvalidFileType: 'Unable to upload a file in the selected format',
    RequestTooLarge: 'The file is too large. Please upload a file up to {0} MB',
    UploadFailed: 'The file was not uploaded. Please try again',
}

function errorText(key: string) {
    return withMaxFileSize(interviewStore.getLocalization(errorKeyPrefix + key) || errorFallbacks[key])
}

const fileToLargeErrorText = computed(() => errorText('FileToLarge'))
const fileTypeInvalidErrorText = computed(() => errorText('InvalidFileType'))

const fileValidationError = ref('')
const successfullyUploadedFiles = ref<Array<UploadedFilesResponse>>([])
const loadedFiles = ref<Array<UploadedFileModel>>([])
const isUploading = ref(false)
const uploadPercent = ref(0)
const isProgressVisible = ref(false)

const fileInput = ref<HTMLInputElement | null>(null)
const progressBar = ref<InstanceType<typeof UploadProgressBar> | null>(null)

/**
 * Bytes-sent is capped at 99. upload.onprogress reaching `total` means the body has left the
 * browser - in practice that it reached the platform edge, not that AddMediaToBackoffice has
 * finished writing into Umbraco media. 100 is set only when the response actually arrives, so
 * the bar never claims completion while work remains.
 */
function reportUploadProgress(percent: number) {
    uploadPercent.value = Math.min(99, Math.floor(percent))
}

const supportedFileTypesString = computed(() => props.supportedFileTypes.map((i) => '.' + i.split('/')[1]).reduce((agg, val) => agg + ' , ' + val))
const nextBtnText = computed(() => interviewStore.getLocalization('InterviewQuestionary.Common.NextQuestionButton'))
const filesUploadBtnText = computed(() => loadedFiles.value.length > 0 ? interviewStore.getLocalization('InterviewQuestionary.UploadFiles.UploadMoreButtonText') : interviewStore.getLocalization('InterviewQuestionary.UploadFiles.UploadButtonText'))
const textAreaPlaceholder = computed(() => interviewStore.getLocalization('InterviewQuestionary.UploadFiles.Edit.Placeholder'))
const textAreaLabel = computed(() => interviewStore.getLocalization('InterviewQuestionary.UploadFiles.Edit.Label'))
const allowedFilesText = computed(() => withMaxFileSize(interviewStore.getLocalization('InterviewQuestionary.UploadFiles.AllowedFilesText')))
// Falls back to untranslated text so the release does not depend on a dictionary edit
// reaching Umbraco, matching the errorFallbacks pattern above.
const uploadProgressLabel = computed(() => interviewStore.getLocalization('InterviewQuestionary.UploadFiles.UploadProgressLabel') || 'Upload progress')
// Hiding Next while an error is displayed is the ticket's explicit requirement:
// the user must not be able to continue past a rejected file.
const isSubmitable = computed(() => loadedFiles.value.length > 0 && !fileValidationError.value)

const root = ref<HTMLElement | undefined>(undefined);
onMounted(() => scrollToElementBottom(root!.value as HTMLElement));

function handleExpolorerOpen() {
    // imitate explorer open
    fileInput.value?.click()
}
/**
 * Splits a selection instead of rejecting it wholesale. The previous implementation threw on
 * the first offending file, so picking a valid image alongside an oversized video discarded
 * both and said nothing about the one that vanished.
 */
function partitionFiles(files: File[]) {
    const accepted: File[] = []
    const tooLarge: File[] = []
    const wrongType: File[] = []

    files.forEach((file) => {
        // A zero-byte file is reported as too large, matching the previous behaviour.
        if (file.size === 0 || file.size > props.maxFileSizeBytes) {
            tooLarge.push(file)
        } else if (!props.supportedFileTypes.includes(file.type)) {
            wrongType.push(file)
        } else {
            accepted.push(file)
        }
    })

    return { accepted, tooLarge, wrongType }
}

/**
 * The dictionary copy is shown verbatim. An earlier version appended the rejected file names
 * in brackets for multi-file selections, which made the rendered message differ from the
 * approved copy - nothing is added to it now, whatever was selected.
 */
function buildValidationError(tooLarge: File[], wrongType: File[]) {
    const messages: string[] = []

    if (tooLarge.length > 0) {
        messages.push(fileToLargeErrorText.value)
    }
    if (wrongType.length > 0) {
        messages.push(fileTypeInvalidErrorText.value)
    }

    return messages.join(' ')
}

function addFiles(files: File[]) {
    const { accepted, tooLarge, wrongType } = partitionFiles(files)

    if (accepted.length > 0) {
        loadedFiles.value = [...loadedFiles.value, ...accepted.map((f) => new UploadedFileModel(f, ''))]
    }

    if (tooLarge.length > 0 || wrongType.length > 0) {
        raiseError(buildValidationError(tooLarge, wrongType))
    }
}

/**
 * Errors are sticky: they are cleared by selecting files, removing a file, or clicking the
 * error block, never by a timer. A timed reset would lift the Next-button gate on its own,
 * letting the user continue without having dealt with the rejected file.
 */
function raiseError(msg: string) {
    fileValidationError.value = msg
}
function clearError() {
    fileValidationError.value = ''
}
async function handleFileUpload() {
    // Mirrors the disabled add-file button: a selection made mid-flight cannot join the
    // request already sending, so it would sit in the list looking uploaded.
    if (isUploading.value) {
        return
    }

    clearError()

    const filelist = fileInput.value?.files ?? []
    const files = [...Array.from(filelist)]

    if (files.length > 0) {
        addFiles(files)
    }
}
function removeImage(file: UploadedFileModel) {
    clearError()
    loadedFiles.value = [...loadedFiles.value.filter((f) => f !== file)]
    if(fileInput.value)
    {
        fileInput.value.value = ""
    }
}
async function submitFiles() {
    if (isUploading.value) {
        return
    }

    isUploading.value = true
    // Reset on entry as well as in the catch: a 413 can arrive before the body finishes
    // transferring, so a retry must not resume from the percentage where the last one stalled.
    uploadPercent.value = 0
    isProgressVisible.value = true

    // The Continue button the user just activated is removed from the DOM by its v-if, so
    // without moving focus here it falls back to <body>, stranding keyboard/screen-reader
    // users. The bar's element does not exist until Vue re-renders, hence the nextTick.
    await nextTick()
    progressBar.value?.focus()

    try {
        const files = await interviewStore.submitMedia(loadedFiles.value, reportUploadProgress)

        // The transfer completed, so the bar earns its 100% and stays visible while the wizard
        // advances. A partial server-side failure below does not change that - those files did
        // reach the server.
        uploadPercent.value = 100

        const uploadedFiles = files.filter(f => f.isUploaded)
        successfullyUploadedFiles.value = [...successfullyUploadedFiles.value,...uploadedFiles]

        interviewStore.changeAnswer(props.question.Id, JSON.stringify(successfullyUploadedFiles.value))

        const unAploadedFileNames = files.filter(f => !f.isUploaded).map(f => f.title).join(', ')

        if (unAploadedFileNames.length) {
            //TODO: mb some translation?
            raiseError(`All files were uploaded, except: ${unAploadedFileNames}`)
        }

        interviewStore.nextQuestion()
        isSubmited.value = true
    } catch (error) {
        // Nothing arrived, so the bar would be lying. The WarningTextBlock is the only
        // message the user should see on this path.
        uploadPercent.value = 0
        isProgressVisible.value = false

        // A coded UploadError picks its own dictionary entry (413 -> RequestTooLarge).
        // Anything else gets the generic entry rather than a raw JS message.
        raiseError(isUploadError(error) ? errorText(error.code) : errorText('UploadFailed'))
    } finally {
        isUploading.value = false
    }
}

watch(loadedFiles, (newValue, oldValue) => {
    if (newValue !== oldValue) {
        emits('handleFilesChanged', [...newValue])
    }
})
watch(fileValidationError, (newValue) => {
    emits('handleError', newValue)
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    ref_key: "root",
    ref: root,
    class: "tw-question tw-question--wide files-upload"
  }, [
    _createElementVNode("input", {
      class: "files-upload__shadow-input",
      type: "file",
      ref_key: "fileInput",
      ref: fileInput,
      onChange: _withModifiers(handleFileUpload, ["prevent"]),
      accept: _unref(supportedFileTypesString),
      multiple: ""
    }, null, 40, _hoisted_1),
    (loadedFiles.value.length > 0)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(loadedFiles.value, (image, index) => {
            return (_openBlock(), _createBlock(ImageEdit, {
              key: index,
              file: image,
              descr: image.description,
              "onUpdate:descr": ($event: any) => ((image.description) = $event),
              placeholder: _unref(textAreaPlaceholder),
              label: _unref(textAreaLabel),
              disabled: isUploading.value || isSubmited.value,
              uploading: isUploading.value,
              onHandleFileRemove: _cache[0] || (_cache[0] = ($event: any) => (removeImage($event)))
            }, null, 8, ["file", "descr", "onUpdate:descr", "placeholder", "label", "disabled", "uploading"]))
          }), 128))
        ]))
      : _createCommentVNode("", true),
    (isProgressVisible.value && loadedFiles.value.length > 0)
      ? (_openBlock(), _createBlock(UploadProgressBar, {
          key: 1,
          ref_key: "progressBar",
          ref: progressBar,
          percent: uploadPercent.value,
          label: _unref(uploadProgressLabel)
        }, null, 8, ["percent", "label"]))
      : _createCommentVNode("", true),
    (!isSubmited.value)
      ? (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
          _createVNode(FlatButton, {
            onClick: handleExpolorerOpen,
            bgColor: '315e3d',
            modelValue: _unref(filesUploadBtnText),
            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => (_isRef(filesUploadBtnText) ? (filesUploadBtnText).value = $event : null)),
            "add-file-btn": true,
            disabled: isUploading.value || isSubmited.value,
            isPlusActive: loadedFiles.value.length > 0
          }, null, 8, ["modelValue", "disabled", "isPlusActive"]),
          _createElementVNode("p", _hoisted_3, _toDisplayString(_unref(allowedFilesText)), 1)
        ], 64))
      : _createCommentVNode("", true),
    (!!fileValidationError.value)
      ? (_openBlock(), _createBlock(WarningTextBlock, {
          key: 3,
          modelValue: fileValidationError.value,
          "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((fileValidationError).value = $event)),
          onClick: clearError
        }, null, 8, ["modelValue"]))
      : _createCommentVNode("", true),
    (_unref(isSubmitable) && !isSubmited.value && !isUploading.value)
      ? (_openBlock(), _createBlock(FlatButton, {
          key: 4,
          onClick: submitFiles,
          modelValue: _unref(nextBtnText),
          "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => (_isRef(nextBtnText) ? (nextBtnText).value = $event : null)),
          bgColor: '#2C267A',
          disabled: isUploading.value,
          isArrowStyle: true
        }, null, 8, ["modelValue", "disabled"]))
      : _createCommentVNode("", true)
  ], 512))
}
}

})